import React, { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import '../dashboard/components/review.css';

const apiUrl = process.env.REACT_APP_API_URL;

const fetchCategoryPage = async (categoryId, page = 1, perPage = 10) => {
  const response = await fetch(`${apiUrl}/api/news?category_id=${categoryId}&page=${page}&per_page=${perPage}`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch page ${page} for category ${categoryId}`);
  }
  
  const data = await response.json();
  return data;
};

const Review = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [allLoadedArticles, setAllLoadedArticles] = useState([]);
  const [copiedItems, setCopiedItems] = useState(new Set());
  const perPage = 10;
  
  const { data: queryData, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['articles', 'category', 4, 'page', currentPage],
    queryFn: () => fetchCategoryPage(4, currentPage, perPage),
    staleTime: 5 * 60 * 1000, // Keep data fresh for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false,
    keepPreviousData: true, // This prevents refetching when changing pages
  });

  // Handle data updates when query succeeds
  React.useEffect(() => {
    if (queryData?.success && queryData?.data && !isLoading) {
      if (currentPage === 1) {
        // First page - replace all articles
        setAllLoadedArticles(queryData.data);
      } else {
        // Additional pages - append to existing articles only if we have new data
        setAllLoadedArticles(prev => {
          // Check if the new data is already in our existing articles to avoid duplicates
          const existingIds = new Set(prev.map(article => article.id));
          const newArticles = queryData.data.filter(article => !existingIds.has(article.id));
          return newArticles.length > 0 ? [...prev, ...newArticles] : prev;
        });
      }
    }
  }, [queryData?.data, currentPage, isLoading]);

  // Use accumulated articles for display
  const newsData = allLoadedArticles;
  const hasMorePages = queryData?.pagination?.has_more || false;

  const handleNewsClick = useCallback((newsItem) => {
    navigate(`/news/${newsItem.id}`, { 
      state: { returnPath: window.location.pathname } 
    });
  }, [navigate]);

  const copyToClipboard = useCallback(async (newsItem, event) => {
    event.stopPropagation();
    
    try {
      const apiEndpoint = `https://inaiq.net/news/${newsItem.id}`;
      await navigator.clipboard.writeText(apiEndpoint);
      
      // Add visual feedback
      setCopiedItems(prev => new Set([...prev, newsItem.id]));
      
      // Remove the mark after 2 seconds
      setTimeout(() => {
        setCopiedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(newsItem.id);
          return newSet;
        });
      }, 2000);
      
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  const handleViewMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  if (isLoading && currentPage === 1) { // Only show loading for first page
    return (
      <div className="review-layout-container">
        <div className="review-content-wrapper">
          <div className="review-news-section">
            <div className="review-loading">جاري التحميل...</div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="review-layout-container">
        <div className="review-content-wrapper">
          <div className="review-news-section">
            <div className="review-error">خطأ في تحميل الأخبار: {error?.message}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="review-layout-container">
      <div className="review-content-wrapper">
        <div className="review-news-section">
          <h2 className="review-section-title">أخبار الفئة الرابعة</h2>
          
          <div className="review-news-list">
            {newsData.length > 0 ? (
              newsData.map((item, index) => {
                return (
                  <div 
                    key={item.id || index} 
                    className="review-news-item"
                    onClick={() => handleNewsClick(item)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="review-news-item-header">
                      <div className="review-news-content">
                        <h3 className="review-news-text">{item.title}</h3>
                      </div>
                      <button 
                        className={`review-copy-btn ${copiedItems.has(item.id) ? 'copied' : ''}`}
                        onClick={(e) => copyToClipboard(item, e)}
                        title="نسخ رابط API"
                      >
                        {copiedItems.has(item.id) ? '✓' : 'نسخ'}
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="review-no-news">لا توجد أخبار متاحة</div>
            )}
          </div>
          
          {/* View More Button */}
          {hasMorePages && (
            <div className="review-view-more">
              <button 
                className="review-view-more-btn"
                onClick={handleViewMore}
                disabled={isFetching}
              >
                {isFetching ? 'جاري التحميل...' : 'عرض المزيد'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Review;